package cc.bluepeak.clubmanager.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import cc.bluepeak.clubmanager.site.Res
import cc.bluepeak.clubmanager.site.components.layouts.PageLayoutData
import cc.bluepeak.clubmanager.site.components.widgets.Accordion
import cc.bluepeak.clubmanager.site.components.widgets.AccordionItem
import cc.bluepeak.clubmanager.site.components.widgets.FieldTag
import cc.bluepeak.clubmanager.site.components.widgets.LineTag
import cc.bluepeak.clubmanager.site.styles.ButtonLinkVariant
import cc.bluepeak.clubmanager.site.styles.PaddedCardStyle
import cc.bluepeak.clubmanager.site.styles.Theme
import cc.bluepeak.clubmanager.site.util.Platform
import cc.bluepeak.clubmanager.site.util.detectPlatform
import com.varabyte.kobweb.compose.css.BackgroundPosition
import com.varabyte.kobweb.compose.css.BackgroundSize
import com.varabyte.kobweb.compose.css.CSSPosition
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.functions.url
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.lucide.LucideArrowRightFromLine
import com.varabyte.kobweb.silk.components.icons.lucide.LucideDownload
import com.varabyte.kobweb.silk.components.icons.lucide.LucideGlobe
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initDownloads(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(Res.string.downloads, Res.string.downloads_title))
}

val DynamicDownloadCard = CssStyle {
    base {
        Modifier.fillMaxWidth()
    }
    Breakpoint.SM {
        Modifier.width(auto)
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun Downloads() {
    val platform = remember { detectPlatform() }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.cssRem)) {
        SimpleGrid(
            numColumns(1, 2), PaddedCardStyle.toModifier().fillMaxWidth()
                .gap(1.cssRem)
                .backgroundImage(url("""/${Res.string.locale}/${Res.image.fabric3}"""))
                .background {
                    size(BackgroundSize.Cover)
                    position(BackgroundPosition.of(CSSPosition.Center))
                }) {
            Column {
                LineTag(Res.string.downloads_tag, Modifier.color(Theme.white))
                H1(Modifier.whiteSpace(WhiteSpace.BreakSpaces).color(Theme.white).toAttrs()) {
                    Text(Res.string.downloads_title)
                }
                Spacer()
                if (platform != Platform.UNKNOWN && platform != Platform.LINUX)
                    Column(PaddedCardStyle.toModifier().then(DynamicDownloadCard.toModifier())) {
                        H2 {
                            Text(Res.string.downloads_for.format(platform.string))
                        }
                        Link(
                            platform.link,
                            variant = ButtonLinkVariant,
                            modifier = Modifier.fillMaxWidth().margin(top = 1.cssRem)
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                                modifier = Modifier.fillMaxHeight()
                            ) {
                                Image(platform.icon, Modifier.height(2.cssRem))
                                SpanText(Res.string.downloads_cta)
                            }
                        }
                    }
            }
            Box(contentAlignment = Alignment.Center) {
                Column(
                    PaddedCardStyle.toModifier().fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(1.cssRem)
                ) {
                    H2(Modifier.margin(bottom = 1.cssRem).toAttrs()) {
                        Text(Res.string.downloads_platforms)
                    }
                    Link(Res.link.windows, variant = ButtonLinkVariant, modifier = Modifier.fillMaxWidth()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = Modifier.fillMaxHeight()
                        ) {
                            Image(Res.image.windows, Modifier.height(2.cssRem))
                            SpanText(Res.string.downloads_windows)
                            Spacer()
                            LucideDownload(size = 1.8.cssRem)
                        }
                    }
                    Link(Res.link.macos, variant = ButtonLinkVariant, modifier = Modifier.fillMaxWidth()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = Modifier.fillMaxHeight()
                        ) {
                            Image(Res.image.apple, Modifier.height(2.cssRem))
                            SpanText(Res.string.downloads_mac)
                            Spacer()
                            LucideDownload(size = 1.8.cssRem)
                        }
                    }
                    Link(Res.link.android, variant = ButtonLinkVariant, modifier = Modifier.fillMaxWidth()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = Modifier.fillMaxHeight()
                        ) {
                            Image(Res.image.android, Modifier.height(2.cssRem))
                            SpanText(Res.string.downloads_android)
                            Spacer()
                            LucideDownload(size = 1.8.cssRem)
                        }
                    }
                    Link(Res.link.ios, variant = ButtonLinkVariant, modifier = Modifier.fillMaxWidth()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = Modifier.fillMaxHeight()
                        ) {
                            Image(Res.image.apple, Modifier.height(2.cssRem))
                            SpanText(Res.string.downloads_ios)
                            Spacer()
                            LucideDownload(size = 1.8.cssRem)
                        }
                    }
                    Link(Res.link.web, variant = ButtonLinkVariant, modifier = Modifier.fillMaxWidth()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = Modifier.fillMaxHeight()
                        ) {
                            LucideGlobe(size = 1.8.cssRem)
                            SpanText(Res.string.downloads_browser)
                            Spacer()
                            LucideArrowRightFromLine(size = 1.8.cssRem)
                        }
                    }
                }
            }
        }

        Column(PaddedCardStyle.toModifier().fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
            FieldTag(Res.string.faq_tag)
            H1(Modifier.margin(bottom = 2.cssRem).toAttrs()) {
                Text(Res.string.faq_title)
            }
            Accordion(
                listOf(
                    AccordionItem(Res.string.faq_size) {
                        P {
                            Text(Res.string.faq_size_answer)
                        }
                    },
                    AccordionItem(Res.string.faq_import) {
                        P {
                            Text(Res.string.faq_import_answer)
                        }
                    },
                    AccordionItem(Res.string.faq_devices) {
                        P {
                            Text(Res.string.faq_devices_answer)
                        }
                    },
                    AccordionItem(Res.string.faq_secure) {
                        P {
                            Text(Res.string.faq_secure_answer)
                        }
                    },
                )
            )
        }
    }
}